import React, {useEffect, useMemo, useRef, useState} from "react";
import styles from "./ProfileMenu.module.scss";
import {getCurrentUsername} from "../../../../api/authContext";
import {UserProfileDto} from "../../../types/user";
import {getCurrentUserProfile} from "../../../../api/profile";
import {logout} from "../../../../api/walletAxios";
import {toInitials} from "../../../utils";

const ProfileMenu = () => {
    const username = getCurrentUsername() || "Player";
    const initial = toInitials(username);

    const [open, setOpen] = useState(false);
    const [profile, setProfile] = useState<UserProfileDto | null>(null);
    const [loading, setLoading] = useState(false);

    const btnRef = useRef<HTMLButtonElement | null>(null);
    const popoverRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!open) return;

        const onMouseDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (popoverRef.current?.contains(target)) return;
            if (btnRef.current?.contains(target)) return;
            setOpen(false);
        };

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };

        document.addEventListener("mousedown", onMouseDown);
        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.removeEventListener("mousedown", onMouseDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);

    const avgPoints = useMemo(() => {
        if (!profile) return null;
        if (!profile.gamesPlayed) return 0;
        return Math.round((profile.pointsTotal / profile.gamesPlayed) * 100) / 100;
    }, [profile]);

    const toggle = async () => {
        const next = !open;
        setOpen(next);

        if (next && !profile && !loading) {
            setLoading(true);
            try {
                const p = await getCurrentUserProfile();
                setProfile(p);
            } finally {
                setLoading(false);
            }
        }
    };

    const onLogout = () => {
        setOpen(false);
        logout();
    };

    return (
        <div className={`${styles.profile}`}>
            <button
                ref={btnRef}
                className={styles.profile__btn}
                onClick={toggle}
                aria-haspopup="dialog"
                aria-expanded={open}
                title="Profile"
            >
                <span className={styles.profile__avatar}>{initial}</span>
            </button>

            {open && (
                <div ref={popoverRef} className={styles.profile__popover} role="dialog">
                    <div className={styles.profile__head}>
                        <div className={styles.profile__name}>{username}</div>
                        <div className={styles.profile__sub}>Your stats</div>
                    </div>

                    {loading && <div className={styles.profile__loading}>Loading...</div>}

                    {!loading && profile && (
                        <div className={styles.profile__stats}>
                            <div className={styles.profile__row}>
                                <span>Games played</span>
                                <b>{profile.gamesPlayed}</b>
                            </div>
                            <div className={styles.profile__row}>
                                <span>Games won</span>
                                <b>{profile.gamesWon}</b>
                            </div>
                            <div className={styles.profile__row}>
                                <span>Avg points per game</span>
                                <b>{avgPoints ?? "-"}</b>
                            </div>
                        </div>
                    )}

                    {!loading && !profile && (
                        <div className={styles.profile__loading}>No profile data yet.</div>
                    )}

                    <div className={styles.profile__actions}>
                        <button className={styles.profile__logoutBtn} onClick={onLogout}>
                            Logout
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfileMenu;
